import englishMessages from 'ra-language-english';
import { getZ3PConfigValue, getServiceEntity } from '../helper';

const serviceLocations = getZ3PConfigValue('service_locations');
const hasWorkplaceServices = serviceLocations?.includes('workplace');

export default {
  ...englishMessages,
  pos: {
    search: 'Search',
    language: 'Language',
    theme: {
      name: 'Theme',
      light: 'Light',
      dark: 'Dark',
    },
    dashboard: {
      monthly_revenue: 'Monthly Revenue',
      new_orders: 'New Orders',
      pending_reviews: 'Pending Reviews',
      new_customers: 'New Customers',
      pending_orders: 'Pending Orders',
      order: {
        items:
          'by %{customer_name}, one item |||| by %{customer_name}, %{nb_items} items',
      },
      welcome: {
        title: 'Welcome to Zippity Admin',
        subtitle:
          "This Admin Console allows you to configure Zippity's offered services, client locations, schedule, and more.",
        home_button: 'Brain Admin',
      },
    },
  },

  auth: {
    back_to_login: 'Back to Login',
    email: 'Email',
    forgot_password: 'Forgot Password?',
    new_password_1: 'Enter New Password',
    new_password_2: 'Repeat New Password',
    passwords_do_not_match: 'Passwords do not match.',
    privacy_policy: 'Privacy Policy',
    set_new_password: 'Set New Password',
    submit: 'Submit',
  },

  resources: {
    'scheduled-work': {
      name: 'Appointment |||| Appointments',
      title: 'Appointment #%{id}:',
      subtitle: '%{service_vehicle_name}',
      fields: {
        date: 'Date',
        created_by_employee_id: 'Created By',
        date_is_null: 'Needs Date',
        service_vehicle_id: `${getServiceEntity()} ID`,
        service_vehicle_name: getServiceEntity(),
        customer_id: 'Customer',
        status: 'Status',
        cancellation_reason: 'Cancellation Reason',
        cancellation_reason_details: 'Cancellation Reason Details',
        created_at: 'Created At',
        modified_at: 'Modified At',
        scheduled_work_id: 'Appointment ID',
        planned_arrival_time: 'Arrival Time',
        planned_departure_time: 'Departure Time',
        confirmed_appointment_start_time: 'Confirmed Appointment Start Time',
        client_location_id: hasWorkplaceServices ? 'Client Location' : 'Shop',
        short_name: 'Short Name',
        keyosk: 'Keyosk',
        parking: 'Parking',
        box: 'Box',
        services: 'Services',
        admin_note: 'Admin Notes',
        total_taxed_price_dollars: 'Total Price',
        amount_due: 'amount_due',
        keys_ready: 'Notify Customer Keys Ready',
        tech_app: 'Tech App',
        health_check: 'Service Report',
        employee_id: 'Employee',
        customer_address_id: 'Address',
        customer_location: 'Customer Location',
        customer_note: 'Customer Note',
        customer_tire_note: 'Customer Tire Note',
        technician_note: 'Technician Note',
        customer_diagnostic_note: 'Customer Diagnostic Note',
        customer_schedule_note: 'Customer Schedule Note',
        key_exchange_note: 'Key Exchange Note',
        vehicle_service_requirements_note: 'Vehicle Service Requirements Note',
        customer_home_additional_note: 'Customer Home Additional Note',
        is_created_by_admin: 'Is Created By Admin',
        is_fleet_service: 'Is Fleet Service',
        long_name: 'Long Name',
        coupon_code: 'Coupon Code',
        custom_discount_reason: 'Custom Discount Reason',
        referrer_code: 'Referrer Code',
        tax_rate_percent: 'Tax Rate',
        prebooked_amount_dollars_excluding_followups:
          'Prebooked Services (excluding "Followups")',
        followup_service_amount_dollars: 'Followups',
        approved_upsell_amount_dollars: 'Approved Upsells',
        rejected_upsell_amount_dollars: 'Rejected Upsells',
        pending_upsell_amount_dollars: 'Pending Upsells',
        future_upsell_amount_dollars: 'Future Upsells',
        total_discounts_dollars: 'Discounts',
        amount_paid_dollars: '$ Paid',
        sales_tax_dollars: 'Sales Tax',
        parking_id: 'Parking',
        service_offering_ids: 'Select Services to Add',
        custom_discount_amount_dollars: 'Custom Discount $',
        keyosk_id: 'Keyosk',
        vehicle_id: 'Vehicle',
        is_handraise: 'Handraise',
        waitlist_note: 'Waitlist Note',
        upcoming_schedule_dates: 'Upcoming Schedule Dates',
        'customer.first_name': 'First Name',
        'customer.last_name': 'Last Name',
        'customer.phone': 'Phone',
        'customer.email': 'Email',
        'customer.default_client_location_id': 'Customer`s Location',
        'customer_address.postal_code': 'Postal Code',
        'vehicle.year': 'Year',
        'vehicle.make': 'Make',
        'vehicle.display_name': 'Vehicle',
        'vehicle.model': 'Model',
        'vehicle.color': 'Color',
        'vehicle.trim': 'Trim',
        'vehicle.engine_name': 'Engine',
        'vehicle.license_plate_number': 'License Plate',
        'vehicle.license_plate_state': 'License Plate State',
        payment_method_id: 'Payment Method',
        tip_amount_dollars: 'Tip',
        is_payment_held: 'Payment on Hold',
      },
      tabs: {
        booking: 'Booking',
        notes: 'Notes',
        customer: 'Customer',
        vehicle: 'Vehicle',
        services: 'Services',
        financial: 'Financial',
        notifications: 'Notifications',
      },
      actions: {
        load_upcoming_schedule_dates: 'Load Upcoming Schedule Dates',
        view_scheduled_work: 'View Appointment',
      },
      filters: {
        date_filter: 'Date filter',
        today: 'Today',
        tomorrow: 'Tomorrow',
        this_week: 'This week',
        last_week: 'Last week',
        custom: 'Custom',
        status: 'Status',
        needs_date: 'Needs date',
        payment_due: 'Payment Due',
        service_vehicle: getServiceEntity(),
        client_location: hasWorkplaceServices ? 'Client Location' : 'Shop',
        customer: 'Customer',
        created_by_employee: 'Created by employee',
      },
      validation: {
        location: 'Please select a location.',
      },
    },
    'customer-address': {
      name: 'Address |||| Addresses',
      title: '%{address_line_1}',
      subtitle: '%{city}, %{state} %{postal_code}',
      fields: {
        tax_rate_percent: 'Tax Rate',
        address_line_1: 'Address line 1',
        address_line_2: 'Address line 2',
        city: 'City',
        state: 'State',
        postal_code: 'Postal Code',
        country: 'Country',
        display_name: 'Display name',
        serviceability_note: 'Serviceability note',
        is_deleted: 'Is Deleted',
        is_primary: 'Is Primary',
        customer_address_id: 'Customer address',
        created_at: 'Created At',
        modified_at: 'Modified At',
        customer_id: 'Email',
      },
    },
    vehicle: {
      name: 'Vehicle |||| Vehicles',
      title: '%{display_name}',
      fields: {
        year: 'Year',
        make: 'Make',
        model: 'Model',
        vehicle_id: 'Vehicle ID',
        customer_id: 'Customer',
        created_at: 'Created At',
        modified_at: 'Modified At',
        color: 'Color',
        trim: 'Trim',
        engine_name: 'Engine',
        vin: 'VIN',
        license_plate_number: 'License Plate Number',
        license_plate_state: 'License Plate State',
        heavy_duty_engine_make: 'Engine Make',
        heavy_duty_engine_serial_number: 'Engine Serial Number',
        fuel_type: 'Fuel Type',
        short_name: 'Short Name',
        email: 'Email',
        is_deleted: 'Deleted?',
        oil_capacities: 'Oil Capacity',
        oil_viscosities: 'Oil Viscosities',
        license_plate_expiration: 'License Plate Expiration',
        inspection_expiration: 'Inspection Expiration',
        bed_type_lengths: 'Bed Type Lengths',
        body_style_types: 'Body Style Types',
        wheel_base_lengths: 'Wheel Base Lengths',
        pt_front_tire_size: 'Front Tire Size',
        pt_rear_tire_size: 'Rear Rire Size',
        pt_base_msrp: 'Base MSRP',
        pt_wheel_base_shortest_inches: 'Wheel Base Shortest (inches)',
        pt_wheel_base_longest_inches: 'Wheel Base Longest (inches)',
        pt_segmentation_description: 'Segmentation Description',
        pt_vehicle_type_description: 'Vehicle Type Description',
        pt_front_tire_pressure_lbs: 'Front Tire Pressure (lbs)',
        pt_rear_tire_pressure_lbs: 'Rear Tire Pressure (lbs)',
        tire_size: 'Tire Size',
        standard_tire_sizes: 'Standard Tire Sizes',
        selected_tire_size: 'Customer Selected Tire Size',
        technician_verified_tire_size: 'Technician Verified Tire Size',
        first_name: 'Customer First Name',
        last_name: 'Customer Last Name',
        customer_email: 'Customer Email',
        'customer.default_client_location_id': 'Location',
        'customer.customer_id': 'Customer',
        motor_oil_spec_api_url: 'Oil Spec',
        motor_tire_spec_api_url: 'Tire Spec',
        is_deleted_value: 'Deleted Vehicle',
        photo_filenames: 'Vehicle Information Photos',
      },
      display: {
        plate: 'Plate',
        mileage: 'Mileage',
        type: 'Vehicle Type',
      },
      tabs: {
        basics: 'Basics',
        third_party_data: '3rd Party Data',
        scheduled_works: 'Appointments',
        customer: 'Customer',
        motor: 'MOTOR Links',
      },
    },
    calendar: {
      name: 'Calendar',
      title: 'Calendar',
    },
    customer: {
      name: 'Customer |||| Customers',
      title: '%{first_name} %{last_name}',
      subtitle: '(%{email})',
      fields: {
        default_client_location_id: 'Location',
        customer_id: 'Customer',
        email: 'Email',
        created_at: 'Created At',
        modified_at: 'Modified At',
        first_name: 'First Name',
        last_name: 'Last Name',
        phone: 'Phone',
        short_name: 'Short Name',
        stripe_customer_id: 'Stripe Customer ID',
        stripe_last4: 'Stripe Last 4',
        stripe_brand: 'Stripe Brand',
        square_customer_id: 'Square Customer ID',
        square_last4: 'Square Last 4',
        square_brand: 'Square Brand',
        cc_customer_id: 'Payment Processor Customer ID',
        cc_last4: 'Credit Card Last 4',
        cc_brand: 'Credit Card Brand',
        gravatar_image_url: 'Gravatar Image',
        how_did_hear: 'Lead Source',
        is_opted_out_of_carfax_reporting: 'Is Opted out of CarFax Reporting',
        notifications_via_email: 'Notifications via email?',
        notifications_via_text: 'Notifications via text?',
        generated_referral_code: 'Generated Referral Code',
        custom_referral_code: 'Custom Referral Code',
        fleet_account_name: 'Fleet Account Name',
        is_fleet_user: 'Is Fleet User?',
        name: 'Name',
      },
      tabs: {
        customer: 'Profile',
        vehicles: 'Vehicles',
        appointments: 'Appointments',
        addresses: 'Addresses',
        timeline: 'Timeline',
        communications: 'Communications',
      },
      sections: {
        contact: 'Contact',
        address: 'Service Address',
        payments: 'Payments',
        service: 'Customer Service',
        preferences: 'Preferences',
      },
      subSections: {
        home_address: 'Home Service Addresses',
        business_address: hasWorkplaceServices
          ? 'Primary Client Location'
          : 'Primary Shop',
        payment: 'Payment Method',
        carfax: 'CarFax Reporting',
        badges: 'Badges',
      },
    },
    'service-vehicle': {
      name: 'Service Vehicle |||| Service Vehicles',
      title: 'Service Vehicle: %{name}',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        service_vehicle_id: 'Service Vehicle ID',
        name: 'Name',
        aap_customer_name: 'AAP Customer Name',
        aap_customer_id: 'AAP Customer ID',
        emoji: 'Emoji',
        slack_channel_name: 'Slack Channel Name',
        quickbooks_class_id: 'QuickBooks Class ID',
        employees: 'Employees',
        default_schedule_types: 'Schedule Types',
        is_active: 'Is Active?',
        transit_radius_minutes: 'Transit radius minutes',
        shorter_transit_radius_minutes:
          'Maximum drive time between two appointments (minutes)',
        hours_to_open_shop: 'Hours to open shop',
        hours_to_close_shop: 'Hours to close shop',
        default_buffer_time_minutes: 'Buffer Time (minutes)',
        home_client_location_id: 'Home Client Location',
        short_name: 'Short Name',
        default_schedule_type_ids: 'Schedule Types',
        employee_ids: 'Employees',
        service_areas: 'Service Area',
        service_area_id: 'Postal Codes',
      },
    },
    'service-bay': {
      name: 'Service Bay |||| Service Bays',
      title: 'Service Bay: %{name}',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        service_vehicle_id: 'Service Bay ID',
        name: 'Name',
        emoji: 'Emoji',
        employees: 'Employees',
        default_schedule_types: 'Schedule Types',
        is_active: 'Is Active?',
        home_client_location_id: 'Shop',
        short_name: 'Short Name',
        default_schedule_type_ids: 'Schedule Types',
        employee_ids: 'Employees',
        default_buffer_time_minutes: 'Buffer Time (minutes)',
      },
    },
    'service-area': {
      name: 'Service Area |||| Service Areas',
      title: 'Service Area: %{id}',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        service_area_id: 'Service Area ID',
        postal_code: 'Postal Code',
        service_vehicles: getServiceEntity(true),
      },
    },
    insights: {
      name: 'Appointment Insights |||| Appointment Insights',
    },
    coupon: {
      name: 'Coupon |||| Coupons',
      title: 'Coupon: %{id}',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        coupon_code: 'Coupon Code',
        applies_to_full_bill: 'Applies to Full Bill',
        amount_off_dollars: 'Absolute Amount Off',
        amount_off_percent: 'Percentage Off',
        start_date: 'Start Date',
        end_date: 'End Date',
        valid_on_date: 'Coupon Valid on Date',
        valid_service_offering_ids: 'Valid Services',
        valid_client_location_ids: `Valid ${
          hasWorkplaceServices ? 'Client Locations' : 'Shop'
        } Locations`,
        coupon_type: 'Coupon Type',
        can_use_multiple_times: 'Customers can use code more than once?',
        minimum_tire_quantity: 'Minimum Tire Quantity',
        maximum_tire_quantity: 'Maximum Tire Quantity',
      },
    },
    integrations: {
      name: 'Integrations',
      title: 'Integrations',
    },
    'pro-signup': {
      name: 'New Pro Signup',
      title: 'New Pro Signup',
    },
    service: {
      name: 'Service |||| Services',
      fields: {
        display_name: 'Display Name',
        created_at: 'Created At',
        status: 'Status',
        is_upsell: 'Upsell',
        upsell_status: 'Upsell Status',
        upsell_rejection_reason: 'Rejection Reason',
        tax_type: 'Tax Type',
        total_cost_dollars: 'Total Charge $',
        parts_cost_dollars: 'Parts Charge $',
        labor_cost_dollars: 'Labor Charge $',
        labor_hours: 'Service Duration (Hours)',
        employee_note: 'Employee Note',
        service_payment_status: 'service_payment_status'
      },
    },
    'service-offering': {
      name: 'Service Catalog |||| Service Catalog',
      title: '%{short_name}',
      fields: {
        short_name: 'Service Name',
        long_name: 'Service Display Name',
        tiny_internal_name: 'Tiny Internal Name',
        schedule_type_id: 'Schedule Types',
        created_at: 'Created At',
        modified_at: 'Modified At',
        service_offering_id: 'Service Offering ID',
        schedule_types: 'Schedule Types',
        marketing_url: 'Marketing URL',
        tax_type: 'Tax Type',
        maintenance_repair_category: 'Maintenance/Repair Category',
        labor_hours: 'Service Duration (Hours)',
        custom_message_1: 'Custom Message 1',
        custom_message_2: 'Custom Message 2',
        custom_message_id_1: 'Custom Message 1',
        custom_message_id_2: 'Custom Message 2',
        add_oil_cost: 'Add oil cost',
        included_with_every_mechanical_service:
          'Automatically added to cart with every service',
        per_tire: 'Is priced per tire',
        inline_image_url: 'Inline Image',
        card_image_url: 'Card Image',
        original_value_dollars: 'Strikethrough Price',
        subtitle: 'Subtitle',
        historical_parts_cost_percentage: 'Historical Parts Cost Percentage',
        schedule_type_ids: 'Schedule Types',
        variable_pricing: 'Starting price',
        display_order: 'Display: Order',
        display_superscript: 'Display: Superscript',
        display_category: 'Service Category',
        display_emphasize: 'Emphasize',
        incompatible_service_offering_ids: 'Incompatible with these services',
        card_bullet_points: 'Bullet Points',
        details_blurb: 'Paragraph',
        more_info_image: 'Image',
        bullet: 'Bullet Point',
        parent_service_offering_id: 'Parent Service',
        is_parent: 'Is Parent service?',
        child_service_offerings: 'Child Services',
        offered_vehicle_makes:
          'Restricted to Vehicle Makes (ONLY available for these makes)',
        bookable: 'Public',
        upsellable: 'Upsellable',
        is_atd_tire_product: 'Is ATD Tire Service?',
        is_tire_installation_fee: 'Is Tire Installation Fee?',
      },
      tabs: {
        basics: 'Basics',
        pricing: 'Pricing',
        display: 'Display (Customer Facing)',
        card: 'Card (Customer Facing)',
      },
    },
    'service-offering-checklist': {
      name: 'Service Checklist |||| Service Checklists',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        checklist_id: 'Checklist ID',
        service_offering_id: 'Service Name',
        instruction: 'Instruction',
      },
    },
    'service-offering-description': {
      name: 'Service Description |||| Service Descriptions',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        service_offering_description_id: 'Description ID',
        service_offering_id: 'Service Name',
        subheader: 'Subheader',
        text: 'Paragraph',
      },
    },
    quote: {
      fields: {
        quote_request: {
          customer_email: 'Customer Email',
          vehicle: {
            display_name: 'Vehicle',
          },
        },
        status: 'Status',
      },
    },
    'quote-request': {
      fields: {
        admin_note: 'Internal notes',
      },
    },
    'client-location': {
      name: `${hasWorkplaceServices ? 'Client' : 'Shop'} Location |||| ${
        hasWorkplaceServices ? 'Client' : 'Shop'
      } Locations`,
      title: `${
        hasWorkplaceServices ? 'Client' : 'Shop'
      } Location: %{short_name}`,
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        client_location_id: hasWorkplaceServices
          ? 'Client Location ID'
          : 'Shop ID',
        is_active: 'Is Active',
        long_name: 'Name',
        short_name: 'Short Name',
        uuid: 'Client Dashboard URL',
        number_potential_customers: '# Potential Customers',
        default_mechanical_service_vehicle_id: `Default ${getServiceEntity()}`,
        default_mechanical_service_vehicle_name: `Default ${getServiceEntity()}`,
        address_line_1: 'Address Line 1',
        address_line_2: 'Address Line 2',
        city: 'City',
        state: 'State',
        postal_code: 'Postal Code',
        logo_image_url: 'Logo Image URL',
        tax_rate_percent: 'Tax Rate',
        price_sheet_name: 'Price Sheet',
        primary_account_manager_employee_id: 'Primary Account Manager',
        avatar_image_url: 'Avatar',
        address_id: 'Address',
        valet_hours: 'Valet Hours',
        secondary_account_manager_employee_id: 'Secondary Account Manager',
        parking_type_id: 'Parking Type',
        operations_notes: 'Operations Notes',
      },
    },
    parking: {
      name: 'Parking |||| Parking Locations',
      title: '%{name}',
      fields: {
        client_location_id: 'Client Location',
        created_at: 'Created At',
        modified_at: 'Modified At',
        parking_id: 'Parking ID',
        name: 'Name',
        blurb: 'Blurb',
        image_url: 'Image URL',
      },
    },
    keyosk: {
      name: 'Keyosk |||| Keyosks',
      title: '%{name}',
      fields: {
        client_location_id: 'Client Location',
        created_at: 'Created At',
        modified_at: 'Modified At',
        keyosk_id: 'Keyosk ID',
        name: 'Name',
        keyosk_type: 'Type',
        'lat-lng': 'Lat/Lng',
        blurb: 'Blurb',
        image_url: 'Image URL',
        number_of_boxes: 'Number of Boxes',
      },
    },
    price: {
      name: 'Price |||| Prices',
      fields: {
        service_offering_id: 'Service Name',
        created_at: 'Created At',
        modified_at: 'Modified At',
        parts_cost_dollars: 'Parts Charge',
        price_sheet_name: 'Price Sheet Name',
        labor_cost_dollars: 'Labor Charge',
        extra_oil_cost_per_qt_dollars:
          'Additional oil cost per quart (over 5qt)',
        price_sheet_id: 'Price Sheet',
      },
    },
    'service-custom-message': {
      name: 'Service Message |||| Service Messages',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        custom_message_id: 'Custom Message ID',
        service_offering_id: 'Service Name',
        internal_name: 'Message Name',
        booking_review_message: 'Checkout Page Message',
        logistics_message: 'Communications Message',
      },
    },
    'technician-blurb': {
      name: 'Inspection Response |||| Inspection Responses',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        name: 'Name',
        inspection_id: 'Inspection',
        inspection_name: 'Inspection Name',
        inspection_condition: 'Inspection Condition',
        diagnostic_question: 'Diagnostic Question',
        blurb: 'Blurb',
        number_of_times_used: 'Number of Times Used',
        created_by: 'Created By (your email)',
      },
    },
    'health-check': {
      name: 'Service Report |||| Service Reports',
      title: '%{name}',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        name: 'Name',
        inspections: 'Inspections',
        inspection_name: 'Inspection Name',
        inspection_ids: 'Inspections',
      },
    },
    inspection: {
      name: 'Inspection Setup |||| Inspection Setup',
      title: '%{inspection_name}',
      fields: {
        condition_required: 'Condition',
        photo_required: 'Photo',
        before_after_photo: 'Before/After Photo',
        overall_measurement: 'Overall Measurement',
        front_driver: 'Front Driver',
        front_passenger: 'Front Passenger',
        rear_driver: 'Rear Driver',
        rear_passenger: 'Rear Passenger',
        notes_required: 'Notes Enabled',
        is_hidden: 'Hide From Customer',
        created_at: 'Created At',
        modified_at: 'Modified At',
        inspection_name: 'Inspection Name',
        display_order: 'Display Order',
        measurement_unit: 'Measurement Unit',
        condition_options: 'Condition Options',
        technician_blurbs: 'Canned Responses',
        suggested_service_offerings: 'Suggested Service Offerings',
        technician_description: 'Technician Description',
        customer_description: 'Customer Description',
        suggested_service_offering_ids: 'Suggested Services',
      },
    },
    employee: {
      name: 'User |||| Users',
      title: '%{first_name} %{last_name}',
      subtitle: '(%{email_address})',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        first_name: 'First Name',
        last_name: 'Last Name',
        email_address: 'Email Address',
        slack_profile_id: 'Slack Profile ID',
        avatar_image_url: 'Avatar',
        role_ids: 'Roles',
      },
    },
    'carfax-record': {
      name: 'Carfax Record |||| Carfax Records',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        carfax_record_id: 'Carfax Record',
        vin: 'VIN',
        mileage: 'Mileage',
        date: 'Date',
        type: 'Type',
        completed_services: 'Completed Services',
        recall_information: 'Recall Information',
        has_recall_been_actioned: 'Has recall been actioned',
        recall_action_internal_note: 'Recall action internal note',
      },
    },
    lead: {
      name: 'Lead |||| Leads',
      fields: {
        default_client_location_id: 'Location',
        has_customer_id: 'Has Converted To Customer?',
        created_at: 'Created At',
        modified_at: 'Modified At',
        lead_id: 'Lead',
        uuid: 'UUID',
        email: 'Email',
        first_name: 'First Name',
        last_name: 'Last Name',
        phone: 'Phone',
        license_plate_number: 'License Plate Number',
        license_plate_state: 'License Plate State',
        internal_note: 'Internal Note',
        client_location_id: hasWorkplaceServices ? 'Client Location' : 'Shop',
        short_name: 'Short Name',
        customer_id: 'Customer',
        source: 'Source',
        source_detail: 'Source Detail',
        source_type: 'Source Type',
      },
    },
    'short-url': {
      name: 'Short URL |||| Short URLs',
      fields: {
        short_url: 'Short URL',
        redirect_url: 'Long URL',
        created_at: 'Created At',
        modified_at: 'Modified At',
        first_clicked_at: 'First Clicked',
        latest_clicked_at: 'Latest Clicked',
        first_ip_address: 'First IP',
        latest_ip_address: 'Latest IP',
        number_of_clicks: 'Number of Clicks',
      },
    },
    'preview-sms': { name: 'SMS |||| SMS', title: 'SMS Preview' },
    mapper: { name: 'Map |||| Maps', title: 'Map' },
    mapbox: { name: 'Map V2 |||| Maps V2', title: 'Map' },
    places: { name: 'Place |||| Places', title: 'Places' },
    'schedule-type': {
      name: 'Schedule Type |||| Schedule Types',
      fields: {
        created_at: 'Created At',
        modified_at: 'Modified At',
        name: 'Name',
        emoji: 'Emoji',
      },
      display: {
        detailing: 'Detailing',
        mechanical: 'Mechanical',
        tires: 'Tires',
        rv: 'RV',
        atv: 'ATV',
        motorcycle: 'Motorcycle',
        plane: 'Plane',
        boat: 'Boat',
      },
    },
    schedule: {
      name: 'Calendar V2',
    },
    zipchat: {
      name: 'Zipchat',
    },
    'gps-tracker': {
      name: 'GPS Tracker',
      title: 'GPS Tracker',
    },
    'payment-method': {
      name: 'Payment Method |||| Payment Methods',
      title: 'Payment Method: %{name}',
      fields: {
        name: 'Name',
        short_description: 'Short Description',
        long_description: 'Long Description',
        is_internal: 'Is Internal?',
        is_active: 'Is Active?',
      },
    },
    pro: {
      name: 'Zippity Pros',
      fields: {
        created_at: 'Created At',
        pro_name: 'Pro Name',
        customer_facing_name: 'Customer Facing Name',
        admin_url: 'Admin URL',
        booking_url: 'Booking URL',
        tech_app_account_name: 'Tech App Account Name',
        status: 'Status',
      },
    },
    'business-configuration': {
      name:
        'Business Configuration Setting |||| Business Configuration Settings',
      headers: {
        app:
          'Customer-Facing information, or settings needed for the booking app',
        'app-recalls': 'Recall Settings',
        'app-schedule': 'Booking App Scheduling Settings',
        admin: 'Settings for the Admin Portal',
        backend: 'Backend Settings',
        email: 'Settings for transactional emails',
        financial: 'Financial Settings',
        other: 'Other Settings',
      },
      fields: {
        at_home_recall_ids_offered: 'Recall IDs that can be serviced at-home',
        available_vehicle_entry_tabs: 'Available Vehicle Entry Tabs',
        bcc_email_address:
          'Admin emails for BCC on transactional emails (if entering multiple, separate with commas)',
        calendar_color: 'Calendar Color',
        call_forwarding_phone_raw: 'Call Forwarding Number',
        client_url: 'Client URL for redirects',
        credit_card_statement_name: 'Stripe credit card statement name',
        theme_primary_color: 'Primary color used in Bookity',
        customer_facing_email:
          'Customer Support Email to be displayed in booking app',
        customer_facing_name: 'Client Name',
        customer_facing_name_short:
          'Shortened Client Name (to be used in reduced space areas)',
        customer_facing_phone:
          'Customer-facing Support Phone Number (can include extra characters)',
        customer_facing_phone_raw:
          'Customer Support Phone Number (no extra characters)',
        customer_facing_address:
          'Physical Address (to be used in all email footers)',
        default_arrival_time: 'Default Arrival Time',
        default_departure_time: 'Default Departure Time',
        default_state: 'Default State (for license plate dropdown)',
        default_vehicle_entry_tab: 'Default Vehicle Entry Tab',
        detailing_price_sheet: 'Detailing Price Sheet',
        google_review_url: 'Google Review URL',
        st_installer_id: 'Simpletire Installer ID',
        my_simple_shop_url: 'My Simple Shop URL',
        has_workplace_service: 'Is offering workplace service?',
        is_accepting_tip: 'Is accepting tip?',
        is_carfax_enabled: 'Is reporting to CarFax?',
        is_hiding_price_in_booking: 'Is hiding price in booking?',
        is_offering_recurring_appointments: 'Is offering repeat appointments?',
        is_skipping_vehicle: 'Is Skipping Vehicle',
        is_using_simple_appointment: 'Is Using Simple Appointment',
        is_using_time_blocks: 'Is using Predetermined Time Blocks?',
        membership_name: 'Membership Program Name',
        performs_inspection_before_service:
          'Performs inspection before service?',
        enable_fleet_in_admin_portal: 'Enable fleet in the Admin Portal',
        show_fleet_in_booking_app: 'Show fleet fields in the Booking App',
        is_performing_recalls: 'Is performing recall services?',
        is_using_waitlist:
          'Is using waitlist functionality? (Let customers book without a date?)',
        logo: 'Logo',
        square_logo: 'Square Logo',
        minimum_onsite_hours:
          'Minimum Onsite Hours (how many hours must a customer be available?)',
        recall_makes_offered: 'Vehicle Makes for Recalls',
        request_tip_at_checkout: 'Request tip at checkout?',
        slack_channel: 'Slack channel name for notifications',
        support_email:
          "Email to use as 'from' address for transactional emails",
        time_range_marks: 'Time Range Marks for scheduling',
        time_range_min: 'Minimum Time offered for scheduling',
        time_range_max: 'Maximum Time offered for scheduling',
        tip_percent_defaults: 'Tip Percent Defaults',
        lead_time_availability_windows_time_of_day: 'Booking Cut-off Time',
        lead_time_availability_windows_number_of_days:
          'How many days before an appointment can a customer book?',
        lead_time_time_slots_number_of_hours:
          'How many hours before an appointment can a customer book?',
        email_background_color:
          'Hex color code for email backgrounds (ex: #d1d3d4)',
        email_button_color: 'Hex color code for email buttons (ex: #3869d4)',
        email_welcome_message: 'Message body for welcome email',
        payment_message_on_checkout_screen:
          'Payment message below price information on checkout screen',
        message_on_schedule_screen:
          'Message below availability selector on schedule screen',
        atd_location_id: 'ATD Location ID',
        show_combined_parts_and_labor_cost: 'Show Combined Parts & Labor Cost',
        review_request_url: 'Review Request URL',
        is_guest_checkout_enabled: 'Is Guest Checkout Enabled?',
        is_detailing_dirty_question_enabled:
          'Is detailing question (about how dirty your car is) enabled?',
        booking_flow_css_file: 'Booking Flow CSS Style',
        is_address_verification_question_enabled:
          'Is address verification question enabled?',
        address_verification_questions: 'Address verification questions',
        is_how_did_hear_question_enabled:
          'Is "How did you hear about us?" question enabled?',
        how_did_hear_options: '"How did you hear about us?" options',
        allows_subusers: 'Allow subusers?',
        allow_additional_user_sms_communications:
          'Allow additional users for SMS communications?',
        terms_and_conditions_url: 'Terms and Conditions URL',
        is_charging_cancellation_fee: 'Charging Cancellation Fee',
        cancellation_cut_off_hours: 'Cancellation Fee Cutoff Hours',
        cancellation_fee_amount_dollars: 'Cancellation Fee Amount',
        no_show_fee_amount_dollars: 'No Show Fee Amount',
        is_showing_quantity_in_booking:
          'Should show quantity field in booking flow?',
        minimum_service_threshold_amount_dollars: 'Minimum Service Threshold',
      },
    },
    dispatch: {
      actions: {
        cancel: 'Cancel',
        send: 'Send 1 Message |||| Send %{smart_count} Messages',
      },
    },
  },
};
